import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useCustomTheme } from '../../../context/ThemeContext';
import { getProfilePhotoUrl } from '../../../utils/imageUtils';
import useLogout from '../../../hooks/useLogout';
import { 
  AppBar, 
  Toolbar, 
  IconButton, 
  Typography, 
  Box, 
  Button, 
  Menu, 
  MenuItem, 
  Avatar, 
  Breadcrumbs, 
  Link, 
  Divider,
  Tooltip
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import NotificationCenter from '../../NotificationCenter';
import LanguageSwitcher from '../../LanguageSwitcher';
import { useNotifications } from '../../../utils/featureFlags';
import { useTranslation } from 'react-i18next';

const Header = ({ onMenuToggle, showMenuButton = true }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const handleLogoutAndRedirect = useLogout();
  const { darkMode, toggleDarkMode } = useCustomTheme();
  const notificationsEnabled = useNotifications();

  const getBreadcrumbs = () => {
    const pathnames = location.pathname.split('/').filter((x) => x);
    const breadcrumbs = [
      {
        label: t('navigation:breadcrumbs.home'),
        path: '/dashboard',
        icon: '🏠',
      },
    ];

    let currentPath = '';
    pathnames.forEach((name, index) => {
      currentPath += `/${name}`;

      // Try to get translation for the path segment
      const translationKey = `navigation:breadcrumbs.${name}`;
      const translatedLabel = t(translationKey);

      // If translation exists (not equal to key), use it; otherwise fall back to title case
      const label = translatedLabel !== translationKey
        ? translatedLabel
        : name.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

      breadcrumbs.push({
        label,
        path: currentPath,
        isLast: index === pathnames.length - 1,
      });
    });

    return breadcrumbs;
  };


  const breadcrumbs = getBreadcrumbs();

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfileClick = (path) => {
    navigate(path);
    handleMenuClose();
  };

  const handleLogoutClick = () => {
    handleLogoutAndRedirect();
    handleMenuClose();
  };

  return (
    <AppBar position="static" color="default" elevation={1} sx={{ backgroundColor: 'background.paper' }}>
      <Toolbar sx={{ justifyContent: 'space-between' }}>
        {/* Left Section */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {showMenuButton && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="Toggle navigation menu"
              onClick={onMenuToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Breadcrumbs */}
          <Breadcrumbs aria-label="Breadcrumb" separator="›" sx={{ display: { xs: 'none', sm: 'flex' } }}>
            {breadcrumbs.map((crumb, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                {crumb.isLast ? (
                  <Typography 
                    variant="body1" 
                    color="text.primary"
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      fontWeight: 'medium'
                    }}
                  >
                    {crumb.icon && (
                      <Box component="span" sx={{ mr: 0.5 }}>
                        {crumb.icon}
                      </Box>
                    )}
                    {crumb.label}
                  </Typography>
                ) : (
                  <Link
                    component="button"
                    variant="body1"
                    underline="hover"
                    color="inherit"
                    onClick={() => navigate(crumb.path)}
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      cursor: 'pointer'
                    }}
                  >
                    {crumb.icon && (
                      <Box component="span" sx={{ mr: 0.5 }}>
                        {crumb.icon}
                      </Box>
                    )}
                    {crumb.label}
                  </Link>
                )}
              </Box>
            ))}
          </Breadcrumbs>
        </Box>

        {/* Right Section - Language Switcher, Theme Toggle, Notifications & Profile Menu */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LanguageSwitcher variant="icon" size="medium" />
          <Tooltip title={darkMode ? t('navigation:theme.switchToLight') : t('navigation:theme.switchToDark')}>
            <IconButton
              onClick={toggleDarkMode}
              color="inherit"
              aria-label="toggle theme"
            >
              {darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
          </Tooltip>
          {notificationsEnabled && <NotificationCenter />}
          <Button
            id="profile-button"
            aria-controls={open ? 'profile-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            onClick={handleMenuOpen}
            endIcon={<KeyboardArrowDownIcon />}
            sx={{ 
              textTransform: 'none',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <Avatar 
              src={getProfilePhotoUrl(user?.profilePhotoThumbnail || user?.profilePhoto)}
              sx={{ 
                width: 32, 
                height: 32, 
                mr: 1, 
                bgcolor: 'primary.main',
                fontSize: '0.875rem'
              }}
            >
              {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
            </Avatar>
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              {user?.firstName} {user?.lastName}
            </Box>
          </Button>
          <Menu
            id="profile-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            MenuListProps={{
              'aria-labelledby': 'profile-button',
            }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center' }}>
              <Avatar 
                src={getProfilePhotoUrl(user?.profilePhotoThumbnail || user?.profilePhoto)}
                sx={{ 
                  width: 40, 
                  height: 40, 
                  mr: 1.5, 
                  bgcolor: 'primary.main' 
                }}
              >
                {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="subtitle1">
                  {user?.firstName} {user?.lastName}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box component="span" sx={{ mr: 0.5 }}>
                    {user?.role === 'admin' && '👑'}
                    {user?.role === 'trainer' && '🏃'}
                    {user?.role === 'receptionist' && '💼'}
                    {user?.role === 'member' && '👤'}
                  </Box>
                  {user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1)}
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ my: 1 }} />
            <MenuItem onClick={() => handleProfileClick('/profile')}>
              <Box component="span" sx={{ mr: 1 }}>👤</Box>
              Profile
            </MenuItem>
            <MenuItem onClick={() => handleProfileClick('/settings')}>
              <Box component="span" sx={{ mr: 1 }}>⚙️</Box>
              Settings
            </MenuItem>
            <Divider sx={{ my: 1 }} />
            <MenuItem onClick={handleLogoutClick} sx={{ color: 'error.main' }}>
              <Box component="span" sx={{ mr: 1 }}>🚪</Box>
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;